import { Inject, Injectable } from '@angular/core'
import { ConfigService } from '../config.service'
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n'

@Injectable( {
    providedIn: 'root'
} )
export class LocalStorageService {

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        public localeService: L10nTranslationService,
        private config: ConfigService
    ) {
    }

    getLocalKey ( key, withApplicationId = true ) {
        let applicationId = null
        if ( withApplicationId ) {
            applicationId = this.getApplicationId()
        }
        let finalKey = this.config.webappName
        if ( applicationId ) {
            finalKey += '_' + applicationId
        }
        finalKey += '_' + key
        return finalKey
    }

    getStorageValue ( key ) {
        return window.localStorage.getItem( this.getLocalKey( key ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( key ) ) ) : null
    }

    setStorageValue ( key, value ) {
        value ? window.localStorage.setItem( this.getLocalKey( key ), JSON.stringify( value ) ) : window.localStorage.removeItem( this.getLocalKey( key ) )
    }

    clear () {
        this.setFavoriteCountryCode( null )
        this.setDefaultCountryCode( null )
        this.setSupportedClientLanguage( null )
        this.setDefaultLang( null )
        this.setOperatorData( null )
        this.setOperatorRoles( null )
        this.setSelectedEndpoint( null )
        this.setSelectedEndpointConfiguration( null )
        this.setCurrentEndpointTimeslots( null )
        this.setSelectedQueue( null )
        this.delSelectedQueuesBooking()
        this.setSelectedPlace( null )
        this.setCurrentPlaceTimeslots( null )
        this.setSelectedAppointmentTypeId( null )
        this.setSelectedGroupSize( null )
        this.setServerTranslations( null )
        this.setInterfaceLanguage( null )
        this.setOperatorBearer( null )
        this.setUserBearer( null )
    }

    getLastReloadInterface () {
        return window.sessionStorage.getItem( this.getLocalKey( 'lastReloadInterface' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'lastReloadInterface' ) ) ) : null
    }

    setLastReloadInterface ( lastReloadInterface ) {
        lastReloadInterface ? window.sessionStorage.setItem( this.getLocalKey( 'lastReloadInterface' ), JSON.stringify( lastReloadInterface ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'lastReloadInterface' ) )
    }

    getFavoriteCountryCode () {
        return window.localStorage.getItem( this.getLocalKey( 'favoriteCountryCode' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'favoriteCountryCode' ) ) ) : this.config.favoriteCountryCode
    }

    setFavoriteCountryCode ( favoriteCountryCode ) {
        favoriteCountryCode ? window.localStorage.setItem( this.getLocalKey( 'favoriteCountryCode' ), JSON.stringify( favoriteCountryCode ) ) : window.localStorage.removeItem( this.getLocalKey( 'favoriteCountryCode' ) )
    }

    getDefaultCountryCode () {
        return window.localStorage.getItem( this.getLocalKey( 'defaultCountryCode' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'defaultCountryCode' ) ) ) : this.config.defaultCountryCode
    }

    setDefaultCountryCode ( defaultCountryCode ) {
        defaultCountryCode ? window.localStorage.setItem( this.getLocalKey( 'defaultCountryCode' ), JSON.stringify( defaultCountryCode ) ) : window.localStorage.removeItem( this.getLocalKey( 'defaultCountryCode' ) )
    }

    getSupportedClientLanguage () {
        return window.localStorage.getItem( this.getLocalKey( 'supportedClientLanguage' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'supportedClientLanguage' ) ) ) : this.config.supportedClientLanguage
    }

    setSupportedClientLanguage ( supportedClientLanguage ) {
        supportedClientLanguage ? window.localStorage.setItem( this.getLocalKey( 'supportedClientLanguage' ), JSON.stringify( supportedClientLanguage ) ) : window.localStorage.removeItem( this.getLocalKey( 'supportedClientLanguage' ) )
    }

    getDefaultLang () {
        return window.localStorage.getItem( this.getLocalKey( 'defaultLang' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'defaultLang' ) ) ) : this.config.defaultLang
    }

    setDefaultLang ( defaultLang ) {
        defaultLang ? window.localStorage.setItem( this.getLocalKey( 'defaultLang' ), JSON.stringify( defaultLang ) ) : window.localStorage.removeItem( this.getLocalKey( 'defaultLang' ) )
    }

    getHomeTab () {
        return window.sessionStorage.getItem( this.getLocalKey( 'homeTab' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'homeTab' ) ) ) : 'current'
    }

    setHomeTab ( homeTab ) {
        homeTab ? window.sessionStorage.setItem( this.getLocalKey( 'homeTab' ), JSON.stringify( homeTab ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'homeTab' ) )
    }

    getTheme () {
        return window.localStorage.getItem( this.getLocalKey( 'theme' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'theme' ) ) ) : null
    }

    setTheme ( theme ) {
        theme ? window.localStorage.setItem( this.getLocalKey( 'theme' ), JSON.stringify( theme ) ) : window.localStorage.removeItem( this.getLocalKey( 'theme' ) )
    }

    getApplicationId () {
        const sessionApplicationId: string = window.sessionStorage.getItem(this.getLocalKey('applicationId', false)) ?
            window.sessionStorage.getItem(this.getLocalKey('applicationId', false)) : null

        if (sessionApplicationId) {
            return sessionApplicationId
        } else {
            const localApplicationId: string = window.localStorage.getItem(this.getLocalKey('applicationId', false)) ?
                window.localStorage.getItem(this.getLocalKey('applicationId', false)) : null
            if (localApplicationId) {
                this.setApplicationId(localApplicationId)
            }
            return localApplicationId
        }
    }

    setApplicationId ( applicationId ) {
        applicationId ?
            window.sessionStorage.setItem(this.getLocalKey('applicationId', false), applicationId) :
            window.sessionStorage.removeItem(this.getLocalKey('applicationId', false))
        applicationId ?
            window.localStorage.setItem(this.getLocalKey('applicationId', false), applicationId) :
            window.localStorage.removeItem(this.getLocalKey('applicationId', false))
    }

    getOperatorData () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorData' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'operatorData' ) ) ) : null
    }

    setOperatorData ( operatorData ) {
        operatorData ? window.localStorage.setItem( this.getLocalKey( 'operatorData' ), JSON.stringify( operatorData ) ) : window.localStorage.removeItem( this.getLocalKey( 'operatorData' ) )
    }

    getOperatorRoles () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorRoles' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'operatorRoles' ) ) ) : null
    }

    setOperatorRoles ( operatorRoles ) {
        operatorRoles ? window.localStorage.setItem( this.getLocalKey( 'operatorRoles' ), JSON.stringify( operatorRoles ) ) : window.localStorage.removeItem( this.getLocalKey( 'operatorRoles' ) )
    }

    getOperatorAccount () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorAccount' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'operatorAccount' ) ) ) : null
    }

    setOperatorAccount ( operatorAccount ) {
        operatorAccount ? window.localStorage.setItem( this.getLocalKey( 'operatorAccount' ), JSON.stringify( operatorAccount ) ) : window.localStorage.removeItem( this.getLocalKey( 'operatorAccount' ) )
    }

    getSelectedEndpoint () {
        return window.localStorage.getItem( this.getLocalKey( 'selectedEndpoint' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'selectedEndpoint' ) ) ) : null
    }

    setSelectedEndpoint ( selectedEndpoint ) {
        selectedEndpoint ? window.localStorage.setItem( this.getLocalKey( 'selectedEndpoint' ), JSON.stringify( selectedEndpoint ) ) : window.localStorage.removeItem( this.getLocalKey( 'selectedEndpoint' ) )
    }

    getSelectedEndpointConfiguration () {
        return window.localStorage.getItem( this.getLocalKey( 'selectedEndpointConfiguration' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'selectedEndpointConfiguration' ) ) ) : null
    }

    setSelectedEndpointConfiguration ( selectedEndpointConfiguration ) {
        selectedEndpointConfiguration ? window.localStorage.setItem( this.getLocalKey( 'selectedEndpointConfiguration' ), JSON.stringify( selectedEndpointConfiguration ) ) : window.localStorage.removeItem( this.getLocalKey( 'selectedEndpointConfiguration' ) )
    }

    getSelectedQueue () {
        return window.localStorage.getItem( this.getLocalKey( 'selectedQueue' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'selectedQueue' ) ) ) : null
    }

    setSelectedQueue ( selectedQueue ) {
        selectedQueue ? window.localStorage.setItem( this.getLocalKey( 'selectedQueue' ), JSON.stringify( selectedQueue ) ) : window.localStorage.removeItem( this.getLocalKey( 'selectedQueue' ) )
    }

    getSelectedQueuesBooking ( queueId ) {
        if ( window.localStorage.getItem( this.getLocalKey( 'selectedQueuesBooking' ) ) ) {
            const value = JSON.parse( window.localStorage.getItem( this.getLocalKey( 'selectedQueuesBooking' ) ) )
            if ( queueId === 'all' ) {
                return value
            } else {
                if ( queueId && value && value[queueId] ) {
                    return value[queueId]
                } else {
                    return null
                }
            }
        } else {
            return null
        }
    }

    setSelectedQueuesBooking ( queueId, selectedQueueBooking ) {
        let queuesBooking = this.getSelectedQueuesBooking( 'all' )
        if ( selectedQueueBooking ) {
            if ( !queuesBooking ) {
                queuesBooking = {}
            }
            queuesBooking[queueId] = selectedQueueBooking
        } else {
            delete queuesBooking[queueId]
        }
        window.localStorage.setItem( this.getLocalKey( 'selectedQueuesBooking' ), JSON.stringify( queuesBooking ) )
    }

    delSelectedQueuesBooking () {
        window.localStorage.removeItem( this.getLocalKey( 'selectedQueuesBooking' ) )
    }

    getSelectedPlace () {
        return window.localStorage.getItem( this.getLocalKey( 'selectedPlace' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'selectedPlace' ) ) ) : null
    }

    setSelectedPlace ( selectedPlace ) {
        selectedPlace ? window.localStorage.setItem( this.getLocalKey( 'selectedPlace' ), JSON.stringify( selectedPlace ) ) : window.localStorage.removeItem( this.getLocalKey( 'selectedPlace' ) )
    }

    getCurrentPlaceTimeslots () {
        return window.localStorage.getItem( this.getLocalKey( 'currentPlaceTimeslots' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'currentPlaceTimeslots' ) ) ) : null
    }

    setCurrentPlaceTimeslots ( currentPlaceTimeslots ) {
        currentPlaceTimeslots ? window.localStorage.setItem( this.getLocalKey( 'currentPlaceTimeslots' ), JSON.stringify( currentPlaceTimeslots ) ) : window.localStorage.removeItem( this.getLocalKey( 'currentPlaceTimeslots' ) )
    }

    getCurrentEndpointTimeslots () {
        return window.localStorage.getItem( this.getLocalKey( 'currentEndpointTimeslots' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'currentEndpointTimeslots' ) ) ) : null
    }

    setCurrentEndpointTimeslots ( currentEndpointTimeslots ) {
        currentEndpointTimeslots ? window.localStorage.setItem( this.getLocalKey( 'currentEndpointTimeslots' ), JSON.stringify( currentEndpointTimeslots ) ) : window.localStorage.removeItem( this.getLocalKey( 'currentEndpointTimeslots' ) )
    }

    getSelectedAppointmentTypeId () {
        return window.sessionStorage.getItem( this.getLocalKey( 'selectedAppointmentTypeId' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'selectedAppointmentTypeId' ) ) ) : null
    }

    setSelectedAppointmentTypeId ( selectedAppointmentTypeId ) {
        selectedAppointmentTypeId ? window.sessionStorage.setItem( this.getLocalKey( 'selectedAppointmentTypeId' ), JSON.stringify( selectedAppointmentTypeId ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'selectedAppointmentTypeId' ) )
    }

    getSelectedGroupSize () {
        return window.sessionStorage.getItem( this.getLocalKey( 'selectedGroupSize' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'selectedGroupSize' ) ) ) : null
    }

    setSelectedGroupSize ( selectedGroupSize ) {
        selectedGroupSize ? window.sessionStorage.setItem( this.getLocalKey( 'selectedGroupSize' ), JSON.stringify( selectedGroupSize ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'selectedGroupSize' ) )
    }

    getCurrentTicket () {
        return window.sessionStorage.getItem( this.getLocalKey( 'currentTicket' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'currentTicket' ) ) ) : null
    }

    setCurrentTicket ( currentTicket ) {
        currentTicket ? window.sessionStorage.setItem( this.getLocalKey( 'currentTicket' ), JSON.stringify( currentTicket ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'currentTicket' ) )
    }

    getTicketToBook () {
        return window.sessionStorage.getItem( this.getLocalKey( 'ticketToBook' ) ) ? JSON.parse( window.sessionStorage.getItem( this.getLocalKey( 'ticketToBook' ) ) ) : null
    }

    setTicketToBook ( ticketToBook ) {
        ticketToBook ? window.sessionStorage.setItem( this.getLocalKey( 'ticketToBook' ), JSON.stringify( ticketToBook ) ) : window.sessionStorage.removeItem( this.getLocalKey( 'ticketToBook' )
        )
    }

    getServerTranslations () {
        return window.localStorage.getItem( this.getLocalKey( 'serverTranslations' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'serverTranslations' ) ) ) : null
    }

    setServerTranslations ( serverTranslations ) {
        serverTranslations ? window.localStorage.setItem( this.getLocalKey( 'serverTranslations' ), JSON.stringify( serverTranslations ) ) : window.localStorage.removeItem( this.getLocalKey( 'serverTranslations' ) )
    }

    getInterfaceLanguage () {
        return window.localStorage.getItem( this.getLocalKey( 'interfaceLanguage' ) ) ? JSON.parse( window.localStorage.getItem( this.getLocalKey( 'interfaceLanguage' ) ) ) : null
    }

    setInterfaceLanguage ( interfaceLanguage ) {
        interfaceLanguage ? window.localStorage.setItem( this.getLocalKey( 'interfaceLanguage' ), JSON.stringify( interfaceLanguage ) ) : window.localStorage.removeItem( this.getLocalKey( 'interfaceLanguage' ) )
    }

    getApiKey ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.getBookingApiKey()
        } else if ( apiName === 'validation' ) {
            return this.getValidationApiKey()
        }
    }

    getBookingApiKey () {
        return window.localStorage.getItem( this.getLocalKey( 'bookingApiKey' ) ) ? window.localStorage.getItem( this.getLocalKey( 'bookingApiKey' ) ) : null
    }

    getValidationApiKey () {
        return window.localStorage.getItem( this.getLocalKey( 'validationApiKey' ) ) ? window.localStorage.getItem( this.getLocalKey( 'validationApiKey' ) ) : null
    }

    setApiKey ( apiName, bookingApiKey ) {
        if ( apiName === 'booking' ) {
            return this.setBookingApiKey( bookingApiKey )
        } else if ( apiName === 'validation' ) {
            return this.setValidationApiKey( bookingApiKey )
        }
    }

    setBookingApiKey ( bookingApiKey ) {
        bookingApiKey ? window.localStorage.setItem( this.getLocalKey( 'bookingApiKey' ), bookingApiKey ) : window.localStorage.removeItem( this.getLocalKey( 'bookingApiKey' ) )
    }

    setValidationApiKey ( validationApiKey ) {
        validationApiKey ? window.localStorage.setItem( this.getLocalKey( 'validationApiKey' ), validationApiKey ) : window.localStorage.removeItem( this.getLocalKey( 'validationApiKey' ) )
    }

    getBearer ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.getUserBearer()
        } else if ( apiName === 'validation' ) {
            return this.getOperatorBearer()
        }
    }

    getUserBearer () {
        return window.localStorage.getItem( this.getLocalKey( 'userBearer' ) ) ? window.localStorage.getItem( this.getLocalKey( 'userBearer' ) ) : null
    }

    getOperatorBearer () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorBearer' ) ) ? window.localStorage.getItem( this.getLocalKey( 'operatorBearer' ) ) : null
    }

    getAccountId ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.getUserId()
        } else if ( apiName === 'validation' ) {
            return this.getOperatorId()
        }
    }

    getUserId () {
        return window.localStorage.getItem( this.getLocalKey( 'userId' ) ) ? window.localStorage.getItem( this.getLocalKey( 'userId' ) ) : null
    }

    getOperatorId () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorId' ) ) ? window.localStorage.getItem( this.getLocalKey( 'operatorId' ) ) : null
    }

    getBearerEnding ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.getUserBearerEnding()
        } else if ( apiName === 'validation' ) {
            return this.getOperatorBearerEnding()
        }
    }

    getUserBearerEnding () {
        return window.localStorage.getItem( this.getLocalKey( 'userBearerEnding' ) ) ? parseInt( window.localStorage.getItem( this.getLocalKey( 'userBearerEnding' ) ), 10 ) : null
    }

    getOperatorBearerEnding () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorBearerEnding' ) ) ? parseInt( window.localStorage.getItem( this.getLocalKey( 'operatorBearerEnding' ) ), 10 ) : null
    }

    getSessionId ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.getUserSessionId()
        } else if ( apiName === 'validation' ) {
            return this.getOperatorSessionId()
        }
    }

    getUserSessionId () {
        return window.localStorage.getItem( this.getLocalKey( 'bookingSessionId' ) ) ? window.localStorage.getItem( this.getLocalKey( 'bookingSessionId' ) ) : null
    }

    getOperatorSessionId () {
        return window.localStorage.getItem( this.getLocalKey( 'operatorSessionId' ) ) ? window.localStorage.getItem( this.getLocalKey( 'operatorSessionId' ) ) : null
    }

    setBearer ( apiName, bearer ) {
        if ( apiName === 'booking' ) {
            return this.setUserBearer( bearer )
        } else if ( apiName === 'validation' ) {
            return this.setOperatorBearer( bearer )
        }
    }

    setOperatorBearer ( operatorBearer ) {
        operatorBearer ? window.localStorage.setItem( this.getLocalKey( 'operatorBearer' ), operatorBearer ) : window.localStorage.removeItem( this.getLocalKey( 'operatorBearer' ) )

        if ( operatorBearer ) {
            const bearerData = this.decodeToken( operatorBearer )
            window.localStorage.setItem( this.getLocalKey( 'operatorSessionId' ), bearerData.jti )
            window.localStorage.setItem( this.getLocalKey( 'operatorId' ), bearerData.operatorId )
            window.localStorage.setItem( this.getLocalKey( 'operatorBearerEnding' ), (bearerData.exp * 1000) + '' )

        } else {
            window.localStorage.removeItem( this.getLocalKey( 'operatorSessionId' ) )
            window.localStorage.removeItem( this.getLocalKey( 'operatorId' ) )
            window.localStorage.removeItem( this.getLocalKey( 'operatorBearerEnding' ) )
        }
    }

    setUserBearer ( userBearer ) {
        userBearer ? window.localStorage.setItem( this.getLocalKey( 'userBearer' ), userBearer ) : window.localStorage.removeItem( this.getLocalKey( 'userBearer' ) )

        if ( userBearer ) {
            const bearerData = this.decodeToken( userBearer )
            window.localStorage.setItem( this.getLocalKey( 'bookingSessionId' ), bearerData.jti )
            window.localStorage.setItem( this.getLocalKey( 'userId' ), bearerData.userId )
            window.localStorage.setItem( this.getLocalKey( 'userBearerEnding' ), (bearerData.exp * 1000) + '' )

        } else {
            window.localStorage.removeItem( this.getLocalKey( 'bookingSessionId' ) )
            window.localStorage.removeItem( this.getLocalKey( 'userId' ) )
            window.localStorage.removeItem( this.getLocalKey( 'userBearerEnding' ) )
        }
    }

    issetApiKey ( apiName ) {
        if ( apiName === 'booking' ) {
            return this.issetBookingApiKey()
        } else if ( apiName === 'validation' ) {
            return this.issetValidationApiKey()
        }
    }

    issetValidationApiKey () {
        const tmpApiKey = this.getValidationApiKey()
        return tmpApiKey && tmpApiKey !== null && tmpApiKey !== 'null' && tmpApiKey !== ''
    }

    issetBookingApiKey () {
        const tmpApiKey = this.getBookingApiKey()
        return tmpApiKey && tmpApiKey !== null && tmpApiKey !== 'null' && tmpApiKey !== ''
    }

    issetUserId ( apiName ) {
        const tmpAccountId = this.getAccountId( apiName )
        return (tmpAccountId && tmpAccountId !== null && tmpAccountId !== 'null' && tmpAccountId !== '') ? true : false
    }

    issetBearer ( apiName ) {
        const tmpBearer = this.getBearer( apiName )
        return (tmpBearer && tmpBearer !== null && tmpBearer !== 'null' && tmpBearer !== '') ? true : false
    }

    tokenIsValid ( apiName ) {
        const tmpBearerEnding = this.getBearerEnding( apiName )
        return (tmpBearerEnding && Date.now() <= tmpBearerEnding) ? true : false
    }

    needToRefreshToken ( apiName ) {
        /*const tmpBearerEnding = this.getBearerEnding( apiName )
        return tmpBearerEnding && Date.now() > ( (new Date( tmpBearerEnding ).getTime()) - this.config.timeToRefreshToken )*/
        return false
    }

    decodeToken ( token ) {
        if ( token && token !== '' ) {
            const base64Url = token.split( '.' )[1]
            const base64 = base64Url.replace( /-/g, '+' ).replace( /_/g, '/' )
            return JSON.parse( window.atob( base64 ) )
        } else {
            return token
        }
    }

    logOut () {
        this.setOperatorBearer( null )
        this.setSelectedEndpoint( null )
        this.clear()
    }

    checkLanguage () {
        let lang = this.getInterfaceLanguage()

        if ( !lang ) {
            lang = this.extractNavigatorLanguage()
            this.setInterfaceLanguage( lang )
        }

        this.localeService.setLocale({ language: lang.replace('_', '-') })

        return lang
    }

    extractNavigatorLanguage () {

        const defaultLang = this.getDefaultLang()

        const languages = window.navigator.languages || new Array( (window.navigator as any).userLanguage )
        for ( const lang of languages ) {
            if (
                Object.keys( this.config.supportedLanguage ).map( el => el.slice( 0, 2 ) ).includes( lang )
                || Object.keys( this.config.supportedLanguage ).includes( lang )
            ) {
                let match = ''
                if ( lang.match( /.*-.*/ ) ) {
                    match = lang
                } else {
                    const objKeys = Object.keys( this.config.supportedLanguage )
                    match = objKeys[objKeys.map( el => el.slice( 0, 2 ) ).indexOf( lang )]
                }
                this.localeService.setLocale({ language: this.config.supportedLanguage[match].replace('_', '-') })
                return this.config.supportedLanguage[match]
            }
        }
        this.localeService.setLocale({ language: defaultLang.replace('_', '-') })
        return defaultLang
    }

    getCurrentSsoInformation() {
        return window.localStorage.getItem(this.getLocalKey('currentSsoInformation')) ? JSON.parse(window.localStorage.getItem(this.getLocalKey('currentSsoInformation'))) : null
    }

    setCurrentSsoInformation(currentSsoInformation) {
        currentSsoInformation ? window.localStorage.setItem(this.getLocalKey('currentSsoInformation'), JSON.stringify(currentSsoInformation)) : window.localStorage.removeItem(this.getLocalKey('currentSsoInformation'))
    }

    getLogLevel() {
        return window.localStorage.getItem(this.getLocalKey('logLevel')) ? JSON.parse(window.localStorage.getItem(this.getLocalKey('logLevel'))) : 0
    }

    setLogLevel(logLevel) {
        logLevel ? window.localStorage.setItem(this.getLocalKey('logLevel'), JSON.stringify(logLevel)) : window.localStorage.removeItem(this.getLocalKey('logLevel'))
    }

}
